"use client";

import { useState } from "react";
import { Link } from "@/i18n/routing";
import { useRouter } from "next/navigation";
import { Search, Home, ShoppingBag, Paintbrush, MessageCircle, ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { BUILDER_COMING_SOON } from "@/lib/constants";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/cart/CartDrawer";

export default function LocaleNotFound() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const t = useTranslations("errors");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      router.push(`/search?q=${encodeURIComponent(q)}`);
    }
  };

  const links = [
    { href: "/", label: t("notFoundHome"), icon: Home },
    { href: "/wigs", label: t("notFoundShop"), icon: ShoppingBag },
    ...(BUILDER_COMING_SOON
      ? []
      : [{ href: "/builder", label: t("notFoundBuilder"), icon: Paintbrush }]),
    { href: "/book", label: t("notFoundContact"), icon: MessageCircle },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <CartDrawer />

      <main className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center">
        {/* Decorative accent */}
        <div className="mb-10 h-px w-16 bg-brand-gold" />

        {/* Label */}
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-brand-gold mb-4">
          Error 404
        </p>

        <h1 className="font-display text-3xl tracking-tight text-brand-dark sm:text-4xl">
          {t("notFoundTitle")}
        </h1>

        <p className="mt-4 max-w-md text-sm leading-relaxed text-brand-muted">
          {t("notFoundMessage")}
        </p>

        {/* Search bar */}
        <form
          onSubmit={handleSearch}
          className="mt-10 flex w-full max-w-md items-center overflow-hidden border border-brand-dark/10 bg-brand-cream/40 transition-all duration-300 focus-within:border-brand-gold focus-within:ring-1 focus-within:ring-brand-gold/20"
        >
          <Search className="ms-4 h-4 w-4 flex-shrink-0 text-brand-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("notFoundSearch")}
            className="flex-1 bg-transparent px-3 py-3.5 text-sm text-brand-dark placeholder:text-brand-muted/50 focus:outline-none"
          />
          <button
            type="submit"
            className="me-1.5 flex h-9 w-9 flex-shrink-0 items-center justify-center bg-brand-gold text-white transition-all duration-300 hover:bg-brand-gold-dark"
          >
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </form>

        {/* Quick links */}
        <nav className="mt-12 grid w-full max-w-lg grid-cols-2 gap-3 sm:grid-cols-4">
          {links.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              className="group flex flex-col items-center gap-3 rounded-sm border border-brand-dark/5 bg-brand-cream/30 px-4 py-5 transition-all duration-300 hover:border-brand-gold/20 hover:bg-brand-gold/[0.03] hover:shadow-sm"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold/[0.06] transition-all duration-300 group-hover:bg-brand-gold/[0.12] group-hover:scale-105">
                <Icon className="h-4 w-4 text-brand-muted transition-colors duration-300 group-hover:text-brand-gold" />
              </div>
              <span className="text-xs font-medium tracking-wide text-brand-dark">{label}</span>
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <Link
          href="/wigs"
          className="mt-12 inline-flex items-center gap-2.5 bg-brand-dark px-10 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-brand-gold active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2"
        >
          {t("notFoundContinue")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>

        {/* Decorative accent */}
        <div className="mt-10 h-px w-16 bg-brand-gold" />
      </main>

      <Footer />
    </div>
  );
}
